<template>
  <BModal
    :id="modalId"
    :title="projectView ? 'Share API.expert project' : 'Share API.expert collection'"
    size="md"
    header-bg-variant="primary"
    scrollable
    :ok-only="true"
    ok-title="Close"
    ok-variant="secondary"
  >
    <link
      rel="stylesheet"
      href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.13.0/css/all.min.css"
      integrity="sha256-h20CPZ0QyXlBuAw7A+KluUYx/3pK+c7lYEpqLTlxjYQ="
      crossorigin="anonymous"
    >
    <p
      v-if="!expanded"
      class="text-center"
    >
      <ShareNetwork
        v-for="network in topNetworks"
        :key="network.network"
        :network="network.network"
        class="btn"
        :style="{
          color: 'white',
          backgroundColor: network.color,
          marginLeft: '0.5em',
          marginBottom: '0.25em',
          display: 'inline-block',
        }"
        :url="sharingUrl"
        :title="sharing.title"
        :description="sharing.description"
        :quote="sharing.quote"
        :hashtags="sharing.hashtags"
        :twitter-user="sharing.twitterUser"
      >
        <i
          :class="network.icon"
          :title="network.name"
        />
        {{ network.name }}
      </ShareNetwork>
    </p>
    <BButton
      v-if="!expanded"
      variant="link"
      @click="expanded = true"
    >
      More...
    </BButton>
    <p
      v-if="expanded"
      class="text-center"
    >
      <ShareNetwork
        v-for="network in networks"
        :key="network.network"
        :network="network.network"
        class="btn"
        :style="{
          color: 'white',
          backgroundColor: network.color,
          marginLeft: '1em',
          marginBottom: '0.75em',
          display: 'inline-block',
        }"
        :url="sharingUrl"
        :title="sharing.title"
        :description="sharing.description"
        :quote="sharing.quote"
        :hashtags="sharing.hashtags"
        :twitter-user="sharing.twitterUser"
      >
        <i
          :class="network.icon"
          :title="network.name"
          style="min-width: 0.8em"
        />
        {{ network.name }}
      </ShareNetwork>
    </p>
    <BButton
      v-if="expanded"
      variant="link"
      @click="expanded = false"
    >
      Less...
    </BButton>
    <p>
      Click to Copy URL:
      <BInputGroup>
        <b-form-input
          ref="copyInput"
          readonly
          :value="sharingUrl"
          style="border-color: var(--blue); cursor:pointer"
          @click="copyToClipboard"
        />
        <b-input-group-append>
          <b-button
            variant="info"
            @click="copyToClipboard"
          >
            <BIconPaperclip />
          </b-button>
        </b-input-group-append>
      </BInputGroup>
    </p>
  </BModal>
</template>

<script>
import { BIconPaperclip } from 'bootstrap-vue';
import { mapGetters } from 'vuex';

export default {
  name: 'ShareModal',
  components: {
    BIconPaperclip,
  },
  props: {
    modalId: {
      type: String,
      required: true,
    },
    projectView: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      expanded: false,
      sharing: {
        url: `${document.location}`,
        title: 'Latest report from API.expert',
        description:
          'API.expert provides actionable API performance and quality API metrics for leading APIs including API uptime, API quality and regional cross-cloud API latency analysis. Whether you’re an API owner or an API developer, API.expert will help you save time, money and customers by delivering access to clear API rankings for all the services you depend on. But you don’t have to be an API expert to use API.expert - simply sign up with one click, and start monitoring leading APIs in your industry today.',
        quote: 'Find out how your APIs compare today!',
        hashtags: 'APIs,API,performance',
        twitterUser: 'api_expert',
      },
      networks: [
        {
          network: 'baidu',
          name: 'Baidu',
          icon: 'fas fah fa-lg fa-paw',
          color: '#2529d8',
        },
        {
          network: 'buffer',
          name: 'Buffer',
          icon: 'fab fah fa-lg fa-buffer',
          color: '#323b43',
        },
        {
          network: 'email',
          name: 'Email',
          icon: 'far fah fa-lg fa-envelope',
          color: '#333333',
          top: true,
        },
        {
          network: 'evernote',
          name: 'Evernote',
          icon: 'fab fah fa-lg fa-evernote',
          color: '#2dbe60',
        },
        {
          network: 'facebook',
          name: 'Facebook',
          icon: 'fab fah fa-lg fa-facebook-f',
          color: '#1877f2',
          top: true,
        },
        {
          network: 'flipboard',
          name: 'Flipboard',
          icon: 'fab fah fa-lg fa-flipboard',
          color: '#e12828',
        },
        {
          network: 'hackernews',
          name: 'HackerNews',
          icon: 'fab fah fa-lg fa-hacker-news',
          color: '#ff4000',
        },
        {
          network: 'instapaper',
          name: 'Instapaper',
          icon: 'fas fah fa-lg fa-italic',
          color: '#428bca',
        },
        {
          network: 'line',
          name: 'Line',
          icon: 'fab fah fa-lg fa-line',
          color: '#00c300',
        },
        {
          network: 'linkedin',
          name: 'LinkedIn',
          icon: 'fab fah fa-lg fa-linkedin',
          color: '#007bb5',
          top: true,
        },
        {
          network: 'odnoklassniki',
          name: 'Odnoklassniki',
          icon: 'fab fah fa-lg fa-odnoklassniki',
          color: '#ed812b',
        },
        {
          network: 'pinterest',
          name: 'Pinterest',
          icon: 'fab fah fa-lg fa-pinterest',
          color: '#bd081c',
        },
        {
          network: 'pocket',
          name: 'Pocket',
          icon: 'fab fah fa-lg fa-get-pocket',
          color: '#ef4056',
        },
        {
          network: 'quora',
          name: 'Quora',
          icon: 'fab fah fa-lg fa-quora',
          color: '#a82400',
        },
        {
          network: 'reddit',
          name: 'Reddit',
          icon: 'fab fah fa-lg fa-reddit-alien',
          color: '#ff4500',
        },
        {
          network: 'skype',
          name: 'Skype',
          icon: 'fab fah fa-lg fa-skype',
          color: '#00aff0',
        },
        {
          network: 'sms',
          name: 'SMS',
          icon: 'far fah fa-lg fa-comment-dots',
          color: '#333333',
        },
        {
          network: 'stumbleupon',
          name: 'StumbleUpon',
          icon: 'fab fah fa-lg fa-stumbleupon',
          color: '#eb4924',
        },
        {
          network: 'telegram',
          name: 'Telegram',
          icon: 'fab fah fa-lg fa-telegram-plane',
          color: '#0088cc',
        },
        {
          network: 'messenger',
          name: 'Messenger',
          icon: 'fab fah fa-lg fa-facebook-messenger',
          color: '#0084ff',
        },
        {
          network: 'tumblr',
          name: 'Tumblr',
          icon: 'fab fah fa-lg fa-tumblr',
          color: '#35465c',
        },
        {
          network: 'twitter',
          name: 'Twitter',
          icon: 'fab fah fa-lg fa-twitter',
          color: '#1da1f2',
          top: true,
        },
        {
          network: 'viber',
          name: 'Viber',
          icon: 'fab fah fa-lg fa-viber',
          color: '#59267c',
        },
        {
          network: 'vk',
          name: 'Vk',
          icon: 'fab fah fa-lg fa-vk',
          color: '#4a76a8',
        },
        {
          network: 'weibo',
          name: 'Weibo',
          icon: 'fab fah fa-lg fa-weibo',
          color: '#e9152d',
        },
        {
          network: 'whatsapp',
          name: 'Whatsapp',
          icon: 'fab fah fa-lg fa-whatsapp',
          color: '#25d366',
        },
        {
          network: 'wordpress',
          name: 'Wordpress',
          icon: 'fab fah fa-lg fa-wordpress',
          color: '#21759b',
        },
        {
          network: 'xing',
          name: 'Xing',
          icon: 'fab fah fa-lg fa-xing',
          color: '#026466',
        },
        {
          network: 'yammer',
          name: 'Yammer',
          icon: 'fab fah fa-lg fa-yammer',
          color: '#0072c6',
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      'colId',
      'selectedDomain',
    ]),
    topNetworks() {
      return this.networks.filter(({ top }) => top);
    },
    sharingUrl() {
      if (this.$route && this.$route.name && this.colId) {
        if (this.projectView) {
          const domain = this.selectedDomain || 'apimetrics.io';
          const path = this.$router.resolve({ name: `collection-domain-${this.colId || 'covid19'}`, params: { domain } }).href;
          const fullUrl = `${window.location.origin}${path}`;
          return fullUrl;
        }

        const path = this.$router.resolve({ name: `collection-${this.colId}` }).href;
        const fullUrl = `${window.location.origin}${path}`;
        return fullUrl;
      }
      return `${document.location}`;
    },
  },
  methods: {
    copyToClipboard() {
      const el = this.$refs.copyInput.$el;
      const selected = document.getSelection().rangeCount > 0
        ? document.getSelection().getRangeAt(0)
        : false;
      el.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          this.$bvModal.msgBoxOk('The URL has been copied to your clipboard');
        } else {
          this.$bvModal.msgBoxOk('Sorry, we we failed to copy to the clipboard');
        }
      } catch (err) {
        this.$bvModal.msgBoxOk('Sorry, we were unable to copy to the clipboard');
      }
      if (selected) {
        document.getSelection().removeAllRanges();
        document.getSelection().addRange(selected);
      }
    },
  },
};
</script>
